import { useMutation } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { ApiError } from '../api/client'
import { admitBooking } from '../api/bookings'
import { useAuth } from '../AuthContext'
import { Icon } from '../../components/Icon'

/**
 * Admin "admit a patient" (M10-T07 UI). The hospital confirms a secured booking
 * when the patient is admitted and the clinical team agrees the bed (ADR-0020).
 * There's no bookings list yet — admins admit by the reference from the patient's
 * confirmation. A hospital_admin may admit only at their own hospital; amtc_admin
 * anywhere (enforced server-side).
 */
export function AdmitBookingPage(): ReactNode {
  const { token } = useAuth()
  const [reference, setReference] = useState('')
  // Admitted in THIS session. There is no bookings list endpoint, so the frame's
  // "Admitted today" panel can only honestly report what this admin has just
  // done — which is what it says.
  const [admitted, setAdmitted] = useState<string[]>([])

  const mutation = useMutation({
    mutationFn: () => admitBooking(token, reference.trim().toUpperCase()),
    onSuccess: (data) => {
      setReference('')
      setAdmitted((prev) => [data.reference, ...prev])
    },
  })

  const trimmed = reference.trim()

  return (
    <section className="flex flex-col gap-5">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink-1">
          Admissions
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-3">
          Admit the patient using the booking reference from their confirmation
          message. Only a secured (paid) booking can be admitted.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-line-1 bg-bg-app p-6">
      <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-ink-1">
        <span
          className="grid h-8 w-8 place-items-center rounded-lg bg-accent-bg text-accent-ink ring-1 ring-inset ring-accent/15"
          aria-hidden="true"
        >
          <Icon name="check" size={16} />
        </span>
        Admit a patient
      </h2>
      <p className="mt-1.5 text-sm text-ink-3">
        The patient can read this from the confirmation SMS or email.
      </p>

      <form
        className="mt-5 flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          if (trimmed.length > 0 && !mutation.isPending) mutation.mutate()
        }}
      >
        <label className="flex flex-col gap-1 text-sm font-medium text-ink-2">
          Booking reference
          <input
            type="text"
            aria-label="Booking reference"
            placeholder="e.g. BK-7H2K9P"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            autoCapitalize="characters"
            className="h-11 rounded-[8px] border border-line-2 bg-bg-app px-3 font-mono text-sm uppercase tracking-wide text-ink-1 placeholder:text-ink-3 placeholder:normal-case focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          />
        </label>

        <button
          type="submit"
          disabled={trimmed.length === 0 || mutation.isPending}
          className="inline-flex h-11 items-center justify-center rounded-[8px] bg-accent px-4 text-sm font-semibold text-white shadow-sm transition-[transform,box-shadow,opacity] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent enabled:hover:-translate-y-0.5 enabled:hover:shadow-lift disabled:opacity-50"
        >
          {mutation.isPending ? 'Admitting…' : 'Admit patient'}
        </button>
      </form>

      {mutation.isSuccess && (
        <p
          role="status"
          className="animate-pop-in mt-4 flex items-center gap-2 rounded-[8px] bg-bed-good-bg px-3 py-2 text-sm text-bed-good ring-1 ring-inset ring-bed-good/15"
        >
          <Icon name="check" size={16} />
          <span>
            Admitted <strong>{mutation.data.reference}</strong> — the patient
            has been notified by SMS.
          </span>
        </p>
      )}
      {mutation.isError && (
        <p
          role="alert"
          className="animate-pop-in mt-4 rounded-[8px] bg-bed-bad-bg px-3 py-2 text-sm text-bed-bad ring-1 ring-inset ring-bed-bad/15"
        >
          {admitErrMsg(mutation.error)}
        </p>
      )}
      </div>

      <section
        aria-labelledby="admitted-heading"
        className="h-fit rounded-2xl border border-line-1 bg-bg-app p-6"
      >
        <header className="flex items-center justify-between gap-4">
          <h2
            id="admitted-heading"
            className="font-display text-lg font-semibold text-ink-1"
          >
            Admitted today
          </h2>
          <span className="rounded-[6px] border border-line-1 px-2 py-0.5 text-sm tabular-nums text-ink-2">
            {admitted.length}
          </span>
        </header>
        <hr className="mt-3 border-line-1" />
        {admitted.length === 0 ? (
          // The frame repeats the input's hint here ("The patient can read this
          // from the confirmation SMS or email") under a heading about
          // admissions. One line that describes the empty list instead.
          <p className="mt-4 rounded-[8px] bg-bg-soft px-3 py-3 text-sm text-ink-3">
            No one has been admitted in this session.
          </p>
        ) : (
          <ul className="mt-4 flex flex-col gap-2">
            {admitted.map((ref) => (
              <li
                key={ref}
                className="flex items-center gap-2 rounded-[8px] bg-bg-soft px-3 py-2 text-sm"
              >
                <Icon name="check" size={14} />
                <span className="font-mono">{ref}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
      </div>
    </section>
  )
}
function admitErrMsg(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'not_admittable':
        return 'That booking isn’t ready to admit — it must be a secured (paid) booking.'
      case 'forbidden_role':
        return 'You don’t administer that booking’s hospital.'
      case 'not_found':
        return 'No booking with that reference.'
      default:
        return err.message
    }
  }
  return 'Something went wrong. Please try again.'
}
